import { MousePointer2, Lock, Check } from "lucide-react";

/**
 * Website Design hero visual: a site that converts, playing on loop.
 * A browser card where a visitor's cursor glides to the CTA, clicks, and a
 * "New lead -> CRM" toast fires; a speed chip and SEO ring anchor the promise.
 * Pure CSS loop (no JS), sample-labeled, reduced-motion falls back to the
 * static assembled state.
 */
export const WebsiteBuildVisual = () => {
  return (
    <div className="relative mx-auto w-full max-w-[500px] select-none">
      <style>{`
        @keyframes wbv-cursor {
          0%, 12% { transform: translate(150px, -70px); }
          32%, 60% { transform: translate(0, 0); }
          78%, 100% { transform: translate(150px, -70px); }
        }
        .wbv-cursor { animation: wbv-cursor 9s cubic-bezier(0.45,0,0.25,1) infinite; }
        @keyframes wbv-press {
          0%, 33%, 41%, 100% { transform: scale(1); }
          36% { transform: scale(0.94); }
        }
        .wbv-cta { animation: wbv-press 9s ease infinite; }
        @keyframes wbv-toast {
          0%, 36% { opacity: 0; transform: translateY(-8px); }
          42%, 78% { opacity: 1; transform: translateY(0); }
          85%, 100% { opacity: 0; transform: translateY(-8px); }
        }
        .wbv-toast { animation: wbv-toast 9s cubic-bezier(0.16,1,0.3,1) infinite; }
        @keyframes wbv-ring { from { stroke-dashoffset: 126; } to { stroke-dashoffset: 2.5; } }
        .wbv-ring { animation: wbv-ring 1.6s cubic-bezier(0.16,1,0.3,1) 0.4s both; }
        @keyframes wbv-shimmer {
          0%, 100% { opacity: 0.55; } 50% { opacity: 1; }
        }
        .wbv-bar { animation: wbv-shimmer 4.5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wbv-cursor, .wbv-cta, .wbv-bar { animation: none; }
          .wbv-toast { animation: none; opacity: 1; transform: none; }
          .wbv-ring { animation: none; stroke-dashoffset: 2.5; }
        }
      `}</style>

      <div className="absolute -inset-8 -z-10 bg-[radial-gradient(circle_at_45%_35%,rgba(47,95,232,0.28),transparent_65%)] blur-2xl" />

      {/* browser card */}
      <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]">
        {/* chrome */}
        <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
          </div>
          <div className="flex flex-1 items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-[11px] font-medium text-slate-500">
            <Lock className="h-3 w-3 text-emerald-500" />
            yourbusiness.ca
          </div>
          <span className="font-mono-ibm rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">Demo</span>
        </div>

        {/* mini page */}
        <div className="relative px-5 py-5">
          {/* toast */}
          <div className="wbv-toast absolute right-4 top-4 z-10 flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 shadow-[0_12px_28px_-10px_rgba(16,19,25,0.25)]">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <span className="text-[11.5px] font-bold text-slate-800">New lead, sent to CRM</span>
          </div>

          {/* nav skeleton */}
          <div className="flex items-center justify-between">
            <div className="h-3 w-16 rounded bg-slate-800" />
            <div className="flex gap-2">
              <div className="wbv-bar h-2 w-9 rounded bg-slate-200" />
              <div className="wbv-bar h-2 w-9 rounded bg-slate-200" style={{ animationDelay: "0.6s" }} />
              <div className="wbv-bar h-2 w-9 rounded bg-slate-200" style={{ animationDelay: "1.2s" }} />
            </div>
          </div>

          {/* hero skeleton */}
          <div className="mt-6 max-w-[75%]">
            <div className="h-4 w-full rounded bg-slate-800/90" />
            <div className="mt-2 h-4 w-3/4 rounded bg-slate-800/90" />
            <div className="mt-3 h-2.5 w-5/6 rounded bg-slate-200" />
            <div className="mt-1.5 h-2.5 w-2/3 rounded bg-slate-200" />

            {/* CTA + cursor */}
            <div className="relative mt-4 inline-block">
              <div className="wbv-cta inline-flex items-center rounded-lg bg-[#2F5FE8] px-4 py-2 text-[12px] font-bold text-white shadow-md shadow-[#2F5FE8]/30">
                Get My Free Quote
              </div>
              <div className="wbv-cursor absolute -right-2 -bottom-2">
                <MousePointer2 className="h-5 w-5 fill-slate-900 text-white drop-shadow" />
              </div>
            </div>
          </div>

          {/* cards row */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
                <div className="h-6 w-6 rounded-lg bg-[#2F5FE8]/15" />
                <div className="mt-2 h-2 w-4/5 rounded bg-slate-300" />
                <div className="mt-1.5 h-2 w-3/5 rounded bg-slate-200" />
              </div>
            ))}
          </div>
        </div>

        {/* status footer */}
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/70 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.7)]" />
            <span className="text-[11px] font-medium text-slate-500">Loads in 0.9s · lead capture wired to your CRM</span>
          </div>
          {/* SEO ring */}
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 48 48" className="h-9 w-9 -rotate-90">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#E2E8F0" strokeWidth="5" />
              <circle className="wbv-ring" cx="24" cy="24" r="20" fill="none" stroke="#10B981" strokeWidth="5" strokeLinecap="round" strokeDasharray="126" />
            </svg>
            <div className="leading-tight">
              <div className="text-[13px] font-bold text-slate-900">98</div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">Speed</div>
            </div>
          </div>
        </div>
      </div>

      {/* floating accent */}
      <div className="absolute -top-4 -right-3 z-10 rounded-full bg-[#2F5FE8] px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-[#2F5FE8]/30">
        Live in 7 days
      </div>
    </div>
  );
};

export default WebsiteBuildVisual;
